import { useState } from "react";

import { ENTRY_REACHOUT_CREDITS, formatCredits } from "@/components/monetization/plans";
import { handToStripe } from "@/components/monetization/stripeNavigation";
import { getSubscriptionPortalUrl } from "@/services/api/monetizationApi";
import type { GuaranteeStanding } from "@/services/api/monetizationApi";
import type { UserSettings } from "../useUserSettings";
import { SettingsSection } from "./SettingsSection";

/**
 * ROW 8 — "My plan and credits" (D-206-010 row 8, kept-editable).
 *
 * **Who does what:** the person sees what his plan costs him per email and how much credit he has
 * left; when he wants to change it, Raisy hands him to the provider's own page rather than running a
 * bespoke billing flow.
 * **What he sees:** his plan, his balance, and the price of one send against that plan.
 * **What he feels:** *"more money, less of my time"* (FTUE W9) — and, from the distrust posture, the
 * relief of a number that is stated rather than a spend he has to discover.
 *
 * THE ONE ROUTE, NOT TWO. `/subscription/my_subscription` is read deliberately: it carries the
 * balance AND the plan AND that plan's per-send price. The balance-only route (`get_credit_balance`)
 * returns no tier — reading it and then reading a second route for the plan is exactly how the tier
 * once ended up unset while the balance rendered fine (measured 2026-09-21, and recorded in the
 * interface's own comment).
 *
 * WHY CHANGING THE PLAN IS A HAND-OFF: cancelling, upgrading and paying are Stripe's pages by
 * decision (D-059) — this product does not re-implement a payment screen, and a person who is about
 * to spend money is shown the page that actually holds his card rather than a convincing copy of it.
 */
/**
 * THE PROMISE, IN THE BUYER'S OWN WORDS (P091 D-132) — or `null`, which means render nothing.
 *
 * Every branch states only what the system EXECUTES, which is the whole reason this surface waited
 * until the grant existed: an "owed" line is honest now because the send gate really does keep sending
 * at our cost until someone answers (D-131). A state we cannot word returns null rather than a
 * reassuring default — a promise phrased from a guess is the one thing this row must never print.
 */
export const guaranteeLine = (guarantee: GuaranteeStanding): string | null => {
  const { term_reachouts: term } = guarantee;
  switch (guarantee.state) {
    case "kept":
      return `First-Conversation Guarantee kept — an investor answered on reachout ${guarantee.first_reply_at_reachout} of your first ${term}.`;
    case "owed":
      return `First-Conversation Guarantee: no investor answered in your first ${term} reachouts, so your sending continues on us until one does.`;
    case "in_progress":
      // "0 of your first 200" reads as a reproach; before the first send the honest line is the term.
      return guarantee.reachouts_sent === 0
        ? `First-Conversation Guarantee: your first ${term} reachouts are covered — if no investor answers in them, your sending continues on us until one does.`
        : `First-Conversation Guarantee: no investor has answered yet in ${guarantee.reachouts_sent} of your first ${term} reachouts — if none does, your sending continues on us until one does.`;
    default:
      return null;
  }
};

export const PlanSection = ({ settings }: { settings: UserSettings }) => {
  const { plan } = settings;
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);

  const openPortal = async () => {
    setOpening(true);
    setOpenError(null);
    try {
      // THE TAB OPENS INSIDE THE CLICK, then Stripe's session fills it — the portal's link also arrives
      // from an `await`, which is exactly what made the old `window.open` blockable. See
      // `stripeNavigation.ts` for the measured gap against glowtest_FE.
      const handoff = await handToStripe(async () => {
        const res = await getSubscriptionPortalUrl();
        return res.data?.url ?? null;
      });
      if (!handoff.ok) setOpenError("the billing page did not answer with a link");
    } catch (err) {
      setOpenError(err instanceof Error && err.message ? err.message : "the billing page could not be reached");
    } finally {
      setOpening(false);
    }
  };

  const value =
    plan.state === "loading" ? (
      <span className="text-muted-foreground">Reading your plan…</span>
    ) : plan.state === "unavailable" ? (
      <span className="text-muted-foreground">
        Not readable right now — {plan.reason}.
      </span>
    ) : (
      <span className="flex flex-col gap-1">
        <span>
          {plan.value.tier ? `${plan.value.tier} plan` : "no plan chosen yet"}
          <span className="ml-2 font-normal text-muted-foreground">
            {typeof plan.value.credit_balance === "number"
              ? `${plan.value.credit_balance.toLocaleString()} credits`
              : "credits not reported"}
            {typeof plan.value.per_send_credit_cost === "number"
              ? ` · ${plan.value.per_send_credit_cost.toLocaleString()} per email`
              : ""}
          </span>
        </span>
        {/* THE PROMISE, WHERE THE PLAN IT BELONGS TO IS ALREADY STATED (D-077 leg 3, D-132).
            It sits on the row that names the plan because the guarantee is a property OF the plan, and
            because the plan is what decides the price of the next email — the two facts a buyer is
            weighing together. Rendered from the closed state, never from a default: `null` (a plan that
            does not sell it) prints nothing at all. */}
        {plan.value.guarantee && guaranteeLine(plan.value.guarantee) && (
          <span className="text-xs font-normal text-muted-foreground">
            {guaranteeLine(plan.value.guarantee)}
          </span>
        )}
        <span className="text-xs font-normal text-muted-foreground">
          {openError
            ? `Could not open your billing page — ${openError}.`
            : "Changing or cancelling your plan happens on the billing page, where your card is."}
        </span>
        {/* WHAT HAPPENS TO THE CREDITS HE ALREADY BOUGHT — said HERE, on the surface he is on when
            he decides to end the plan, and not only on the card where he buys them (D-107, 2026-09-23).
            The rate follows the plan, and cancelling does not cancel the balance: it stays and spends
            at the entry rate, which is the ladder's first rung (196 on a 49,000 grant) and already this
            file's own vocabulary. DERIVED, so a re-based ladder cannot leave this sentence lying —
            D-081 moved these rates 5x down once already.
            THE WORDING IS THE DECISION, not decoration: it is a fact about the BALANCE ("your credits
            stay ... they spend at"), never about the person ("if you leave", "you will lose"), because
            a sentence whose subject is his absence reads as a scolding and fails the psychology gate. */}
        {plan.value.tier && typeof plan.value.credit_balance === "number" && plan.value.credit_balance > 0 && (
          <span className="text-xs font-normal text-muted-foreground">
            Your {formatCredits(plan.value.credit_balance)} credits stay in your balance if the plan ends.
            They keep working — at the entry rate of {formatCredits(ENTRY_REACHOUT_CREDITS)} per email
            once there is no plan
            {typeof plan.value.per_send_credit_cost === "number"
              ? `, instead of ${formatCredits(plan.value.per_send_credit_cost)}`
              : ""}
            .
          </span>
        )}
      </span>
    );

  return (
    <SettingsSection
      title="My plan and credits"
      hint="What your plan is, what one email costs you on it, and how much credit is left."
      value={value}
      control={
        <button
          type="button"
          onClick={() => void openPortal()}
          disabled={opening}
          className="rounded-md border border-border bg-background px-2 py-1 text-sm font-medium disabled:opacity-60"
        >
          {opening ? "Opening…" : "Change plan or add credits"}
        </button>
      }
    />
  );
};
